import { REF_FREQ } from '@muzly/voice-engine';

import type { PitchGraphPoint } from '../../components';

/**
 * sustainGuide — 지속음 진단(`아~ 길게 부르기`)의 판정. 순수 함수다.
 *
 * 이 진단이 재는 것은 **MPT(한 호흡으로 얼마나 길게 내나)**다 — 어떤 음이냐가 아니다.
 * 그래서 트랙 채움은 **발성 여부**로 정해지고, 기준음은 채움 조건이 아니라 표식의
 * **세로 축 중심**이다: 지금 내는 음이 기준 대비 어디쯤인지를 보여준다(시안 주석
 * `현재 피치 (위/아래, 회색/붉은)`).
 *
 * 화면은 기준음 '라(A)'를 **들려주고 문구로도 부른다**(`web.diagnosis.tests.sustain.desc`,
 * 가이드 카드의 미리듣기). 예전에는 안내가 "편안한 높이"만 요구하면서 표식은 고정 A3
 * 대비로 움직여, 사용자가 아무 음이나 내고 화면은 그걸 벗어남으로 그렸다
 * (`.claude/20260907/DEBUG-16-57-35.md`).
 *
 * 판정 둘이 여기서 나온다.
 * 1. `latchedTarget` + `deviationRatio` — 축의 기준음을 테이크 시작에 못 박고, 그 기준
 *    대비 얼마나 벗어났나(−1..1)를 잰다. 표식의 세로 위치다.
 * 2. `sustainIdleSec` — 발성이 끊긴 채로 몇 초가 지났나. 상태기계의 자동 종료 근거다.
 *
 * React·시간 의존 없음 — vitest 대상.
 */

/**
 * 기준음(MIDI). 엔진의 `REF_FREQ`(A4 = 440Hz)에서 **유도한다** — 숫자 69를 여기 박아
 * 두면 안 된다.
 *
 * 이유: `REF_FREQ`는 화면이 **소리로 들려주는** 음이고(가이드 카드의 미리듣기 =
 * `reference-tone-a4.wav`), 화면이 사용자에게 내라고 요구하는 음은 그것과 **반드시 같아야**
 * 한다. 두 자리에 따로 적어 두면 한쪽만 옮겼을 때 화면은 라를 들려주고 표식은 다른 음을
 * 기준으로 도는 상태가 조용히 생긴다.
 *
 * **옥타브는 축의 출발점일 뿐이다** — 실제 축의 기준은 `latchedTarget`이 첫 발성에서
 * 가장 가까운 옥타브의 라로 내려 잡으므로, 성별·음역에 따라 편한 옥타브가 달라도 같은
 * 축을 쓴다. 그래서 설문의 성별로 분기할 필요가 없다("안 밝힘"이 선택지라 모든 회차에서
 * 답이 오지도 않는다).
 */
export const SUSTAIN_TARGET_MIDI = 69 + 12 * Math.log2(REF_FREQ / 440);

/**
 * 기준음 미리듣기로 트는 음원. `REF_FREQ`와 **같은 음이어야 한다** — 들려주는 음과
 * 표식의 축이 갈리면 화면이 두 가지 기준을 말한다.
 *
 * 팀이 준 `라음.wav`(A4, 실측 440~443Hz)를 다듬은 사본이다: 원본은 3초 중 뒤 2초가
 * −43dBFS 아래로 떨어지는 잔향 꼬리라 재생 버튼이 안 들리는 동안 계속 "재생 중"으로
 * 남았고, 피크도 −15.7dBFS라 휴대폰 스피커에서 기준음으로 삼기엔 작았다. 1.3초로 자르고
 * +12dB, 끝 0.2초 페이드아웃 — 음정은 그대로다(실측 440~443Hz).
 */
export const SUSTAIN_TONE_URL = '/diagnosis/reference-tone-a4.wav';

/**
 * 표식이 화면 위/아래 끝에 닿는 벗어남(cents). 600 = 반옥타브.
 *
 * 이 값이 곧 세로 축의 스케일이다. 좁으면 조금만 어긋나도 표식이 끝에 붙어 얼마나
 * 벗어났는지 못 읽고, 넓으면 반음 차이가 눈에 안 보인다.
 *
 * 반옥타브인 것이 `latchedTarget`(아래)과 **짝이다**: 축의 기준이 사용자가 실제로 낸
 * 옥타브의 라로 내려앉으므로, 그 라 언저리를 유지하는 동안 표식이 끝에 박히지 않는다.
 */
export const DEVIATION_SPAN_CENTS = 600;

/** 두 MIDI 값 사이의 cents 차. 반음 = 100 cents. */
export const centsBetween = (midi: number, targetMidi: number): number =>
  (midi - targetMidi) * 100;

/**
 * 축의 기준을 정하는 데 쓰는 첫 발성 프레임 수. 중앙값을 쓰므로 홀수다.
 *
 * 1프레임으로 정하지 않는 이유: 발성 시작 직후는 트래커가 옥타브를 헛짚기 쉬운 구간이고
 * (`.claude/20260805/DEBUG-15-49-40.md`에서 시작 직후 오검출 84.3이 축을 통째로 가져갔다),
 * 축은 테이크 내내 안 바뀌므로 한 번 잘못 잡으면 테이크 전체가 어긋난다. 5프레임(≈100ms)
 * 중앙값이면 단발 오검출은 못 이긴다.
 */
export const LATCH_FRAMES = 5;

/**
 * 축의 기준음(MIDI) — **사용자가 실제로 낸 옥타브의 '라'**. 아직 정할 만큼 발성이 없으면
 * `null`.
 *
 * 진단이 요구하는 것은 "라를 내라"이지 "A4를 내라"가 아니다. 그래서 축의 기준은
 * `targetMidi`(A4) 그 자체가 아니라, 첫 발성에 가장 가까운 옥타브의 라다 — A3를 내는
 * 남성도 A5를 내는 소프라노도 가운데에서 시작한다.
 *
 * **옥타브를 접지 않는다.** 예전에는 매 프레임 가장 가까운 A로 접어(`foldToNearestA`)
 * 같은 효과를 냈는데, 그러면 축이 pitch-class 축(mod 12)이 되어 **연속 상승이
 * 톱니가 됐다**: 기준에서 반옥타브(D#)를 넘는 순간 표식이 꼭대기에서 바닥으로 떨어졌다
 * (`.claude/20260916/DEBUG-14-18-00.md`). 축을 테이크 시작에 한 번 못 박으면 그 뒤로는
 * 음높이와 세로 위치가 단조롭게 같이 움직인다.
 */
export const latchedTarget = (
  points: PitchGraphPoint[],
  targetMidi = SUSTAIN_TARGET_MIDI,
): number | null => {
  const first: number[] = [];
  for (const { midi } of points) {
    if (midi != null) {
      first.push(midi);
      if (first.length === LATCH_FRAMES) {
        break;
      }
    }
  }
  if (first.length < LATCH_FRAMES) {
    return null;
  }
  const sorted = [...first].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  return targetMidi + 12 * Math.round((median - targetMidi) / 12);
};

/**
 * 기준음 대비 세로 위치(−1..1). 양수 = 기준보다 높다.
 *
 * 날 것의 거리를 쓰고 축 밖은 clamp한다 — 접지 않는 이유는 `latchedTarget` 주석 참고.
 * 옥타브 보정은 `targetMidi`를 고르는 쪽(= `latchedTarget`)의 몫이다.
 */
export const deviationRatio = (
  midi: number,
  targetMidi = SUSTAIN_TARGET_MIDI,
): number => {
  const ratio = centsBetween(midi, targetMidi) / DEVIATION_SPAN_CENTS;
  return Math.max(-1, Math.min(1, ratio));
};

/**
 * 마지막 **발성** 시점 이후 흐른 시간(초).
 *
 * MPT 진단이라 어떤 음을 내든 소리를 내는 동안은 측정이 진행 중이다 — 발성이 끊긴 채
 * 5초가 지나면(시안: "5초동안 없으면 자동 종료") 상태기계가 테이크를 끝낸다.
 *
 * 아직 한 번도 발성이 없었으면 **테이크 시작부터** 잰다: 첫 소리를 영영 안 내는
 * 경우에도 화면이 스스로 끝나야 한다. 점이 하나도 없으면 0이다(아직 아무것도 안 흘렀다).
 */
export const sustainIdleSec = (
  points: PitchGraphPoint[],
  /**
   * 조용함을 재기 시작할 **바닥 시각**(캡처 타임라인). 일시 중단에서 재개한 시각이
   * 들어온다 — 멈춰 있는 동안에도 타임라인은 흐르므로, 바닥이 없으면 30초 멈췄다
   * 재개한 순간 "30초째 조용하다"가 되어 테이크가 곧바로 끊긴다.
   */
  notBeforeMs = 0,
): number => {
  if (points.length === 0) {
    return 0;
  }
  const now = points[points.length - 1].t;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const { t, midi } = points[i];
    if (midi != null) {
      return (now - Math.max(t, notBeforeMs)) / 1000;
    }
  }
  return (now - Math.max(points[0].t, notBeforeMs)) / 1000;
};
